import { CssBaseline, GlobalStyles } from "@mui/material";
import React, { ReactNode, useEffect } from "react";
import { ThemeProvider, createTheme, responsiveFontSizes, useColorScheme } from "@mui/material/styles";
import type {} from "@mui/material/themeCssVarsAugmentation";

import { CacheProvider } from "@emotion/react";
import createCache, { EmotionCache } from "@emotion/cache";
import { translateNumbersToLowercaseLetters } from "./Helpers/translateNumbersToLowerCase/translateNumbersToLowerCase";
import { useKeyboardToggle } from "./Helpers/useKeyboardToggle";
import { Action, DevPanel, sendMessageToReactNative, useRootFontSize } from "./Helpers/helpers";
import paletteLight from "./paletteLight";
import paletteDark from "./paletteDark";

declare module "@mui/material/styles" {
  interface ShadowPaletteColor extends PaletteColor {
    white?: string;
    black?: string;
  }
  interface Palette {
    tertiary: Palette["primary"];
    shadow: ShadowPaletteColor;
  }

  interface PaletteOptions {
    tertiary: PaletteOptions["primary"];
    shadow: ShadowPaletteColor;
  }

  interface PaletteColor {
    darker?: string;
    lighter?: string;
  }

  interface Shape {
    borderRadius: number;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    shadow: true;
    tertiary: true;
    success: true;
  }
}

const bnMuiTheme = (
  htmlFontSize: number,
  scaledFontSize: number,
  isApp?: boolean,
  darkThemeEnabled?: boolean,
  customizedTypography?: boolean,
) => {
  const zoom = scaledFontSize / htmlFontSize;
  const toRem = (px: number | string, factor: number = 1) => {
    const numeric = typeof px === "string" ? parseInt(px, 10) : px;
    return `${(numeric * factor) / htmlFontSize}rem`;
  };

  const bnTheme = createTheme({
    cssVariables: { nativeColor: true, colorSchemeSelector: "data" },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600 * zoom,
        md: 960 * zoom,
        lg: 1280 * zoom,
        xl: 1920 * zoom,
      },
    },
    typography: {
      fontFamily: ["ApercuPro", "Helvetica", "sans-serif"].join(","),
      htmlFontSize: htmlFontSize,
      fontWeightBold: "bold",
    },
    spacing: (factor) => toRem(8 * factor),
    palette: !darkThemeEnabled ? paletteLight : undefined,
    colorSchemes: darkThemeEnabled
      ? {
          light: { palette: paletteLight },
          dark: { palette: paletteDark },
        }
      : undefined,
    shape: {
      borderRadius: 4,
    },
    components: {
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) =>
            theme.applyStyles("dark", {
              "& .MuiSvgIcon-root": {
                color: "#B9DDED",
              },
              '& img[src$=".svg"]': {
                filter: "invert(1) hue-rotate(180deg)",
              },
            }),
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            boxShadow: "none !important",
            "&:focus-visible, &.Mui-focusVisible": {
              outline: "auto",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: toRem(theme.shape.borderRadius, 4),
          }),
        },
        defaultProps: {
          elevation: 0,
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            zIndex: "1",
          },
        },
      },
      MuiGrid: {
        defaultProps: {
          spacing: 2,
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: toRem(14),
            fontWeight: "bold",
            "&.Mui-error": { color: theme.vars.palette.error },
          }),
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: [
            ({ theme }) => ({
              padding: `${toRem(24)} ${toRem(12)}`,
              backgroundColor: theme.vars.palette.shadow.light,
              border: `${toRem(1)} solid ${theme.vars.palette.tertiary.light}`,
            }),
            ({ theme }) =>
              theme.applyStyles("dark", {
                backgroundColor: theme.vars.palette.shadow.darker,
                borderColor: theme.vars.palette.tertiary.dark,
                color: `${theme.vars.palette.shadow.contrastText}`,
                ".MuiTypography-root": {
                  color: `${theme.vars.palette.shadow.contrastText}`,
                },
              }),
          ],
          positionStart: ({ theme }) => ({
            borderRight: 0,
            marginRight: 0,
            borderTopLeftRadius: toRem(theme.shape.borderRadius),
            borderBottomLeftRadius: toRem(theme.shape.borderRadius),
          }),
          positionEnd: ({ theme }) => ({
            marginLeft: 0,
            borderLeft: 0,
            borderTopRightRadius: toRem(theme.shape.borderRadius),
            borderBottomRightRadius: toRem(theme.shape.borderRadius),
          }),
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: toRem(14),
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.vars.palette.text.primary,
            "&.Mui-checked": {
              color: theme.vars.palette.text.primary,
            },
          }),
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            width: toRem(32),
          },
          select: ({ theme }) => ({
            paddingRight: `${toRem(36)} !important`,
            "&:focus": {
              borderRadius: toRem(theme.shape.borderRadius),
            },
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: toRem(theme.shape.borderRadius, 4),
          }),
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          input: {
            "&.MuiInput-input.MuiInput-input": {
              padding: `${toRem(10)} ${toRem(12)}`,
            },
            height: toRem(20),
          },
          inputRoot: {
            "& .MuiCircularProgress-root": {
              position: "absolute",
              right: toRem(30),
            },
            paddingBottom: "0",
          },
          endAdornment: ({ theme }) => ({
            padding: `${toRem(6)} ${toRem(2)} ${toRem(7)} ${toRem(1)}`,
            maxHeight: "100%",
            border: `${toRem(1)} solid ${theme.vars.palette.shadow.main}`,
            borderLeft: 0,
            transition: theme.transitions.create(["border-color", "background-color"]),
            borderTopRightRadius: toRem(theme.shape.borderRadius),
            borderBottomRightRadius: toRem(theme.shape.borderRadius),
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.MuiInputBase-formControl": {
              marginTop: theme.spacing(3),
            },
            "&:before,&:after": {
              display: "none !important",
            },
            "&.Mui-disabled .MuiInput-input": {
              borderColor: theme.vars.palette.shadow.main,
            },
            "&:not(.Mui-disabled):hover .MuiInputAdornment-standard,&.Mui-focused .MuiInputAdornment-standard,&.Mui-focused .MuiAutocomplete-endAdornment":
              {
                borderColor: theme.vars.palette.secondary.dark,
              },
            "&.Mui-focused .MuiInputAdornment-standard": {
              border: `${toRem(2)} solid ${theme.vars.palette.secondary.dark}`,
              borderRight: "none",
              padding: `${toRem(23)} ${toRem(11)}`,
            },
            "&.Mui-focused .MuiInputAdornment-positionEnd.MuiInputAdornment-standard": {
              borderRight: `${toRem(2)} solid ${theme.vars.palette.secondary.dark}`,
              borderLeft: "none",
            },
            "&.Mui-error:hover .MuiInputAdornment-standard,&.Mui-error .MuiInputAdornment-standard": {
              borderColor: theme.vars.palette.error.main,
            },
            "&:not(.Mui-disabled):hover .MuiInput-input,.MuiInput-input:focus": {
              borderColor: theme.vars.palette.secondary.dark,
            },
            ".MuiInput-input:focus": {
              borderColor: theme.vars.palette.secondary.dark,
              borderWidth: toRem(2),
              padding: toRem(13),
            },
            "&.MuiInputBase-adornedEnd .MuiInput-input:focus": {
              borderRight: 0,
              borderBottomRightRadius: 0,
              borderTopRightRadius: 0,
            },

            "&.Mui-error .MuiInput-input": {
              borderColor: theme.vars.palette.error.main,
            },
            "&.MuiInputBase-sizeSmall": {
              marginTop: 0,
            },
          }),

          input: ({ theme }) => [
            {
              borderRadius: toRem(theme.shape.borderRadius, 1.2),
              position: "relative",
              border: `${toRem(1)} solid ${theme.vars.palette.tertiary.light}`,
              fontSize: toRem(16),
              fontWeight: "normal !important",
              padding: toRem(14),
              backgroundColor: theme.vars.palette.common.white,
              height: toRem(20),
            },
            theme.applyStyles("dark", {
              backgroundColor: `color-mix(in srgb, ${theme.vars.palette.shadow.white} 10%, transparent)`,
              borderColor: theme.vars.palette.tertiary.dark,
            }),
            {
              variants: [
                {
                  props: ({ ownerState }) => Boolean(ownerState.startAdornment),
                  style: {
                    borderLeft: "0",
                    borderBottomLeftRadius: "0",
                    borderTopLeftRadius: "0",
                    fontWeight: "bold",
                  },
                },
                {
                  props: ({ ownerState }) => Boolean(ownerState.endAdornment),
                  style: {
                    borderRight: "0",
                    borderBottomRightRadius: "0",
                    borderTopRightRadius: "0",
                    fontWeight: "bold",
                  },
                },
                {
                  props: ({ ownerState }) => Boolean(ownerState.hiddenLabel),
                  style: {
                    marginTop: 0,
                  },
                },
              ],
            },
          ],
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme }) => [
            {
              fontSize: `${toRem(14)} !important`,
              fontWeight: "bold",
              transform: "none !important",
              lineHeight: toRem(20),
              i: {
                fontWeight: "normal",
                fontStyle: "normal",
                marginLeft: toRem(10),
              },
              "&.Mui-focused": {
                color: theme.vars.palette.text.primary,
              },
            },
            theme.applyStyles("dark", {
              "&.Mui-focused": {
                color: theme.vars.palette.common.white,
              },
            }),
          ],
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: toRem(14),
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: ({ theme }) => ({
            marginTop: theme.spacing(1),
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: {
          arrow: { color: "#d81939" },
          tooltip: {
            fontSize: toRem(14),
            backgroundColor: "#d81939",
            fontWeight: "normal",
            paddingTop: toRem(5),
            paddingBottom: toRem(5),
            paddingLeft: toRem(15),
            paddingRight: toRem(15),
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            "&.Mui-expanded": { minHeight: toRem(48) },
          },
          content: {
            "&.Mui-expanded": { margin: `${toRem(12)} 0` },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: ({ theme }) => ({
            "&.Mui-focusVisible": {
              backgroundColor: "rgba(0, 61, 80, 0.30)",
            },
            color: theme.vars.palette.shadow.main,
            [":not(.Mui-checked) .MuiSwitch-thumb"]: {
              color: theme.vars.palette.shadow.main,
            },
            [":not(.Mui-checked)+.MuiSwitch-track"]: {
              backgroundColor: theme.vars.palette.shadow.main,
            },
          }),
        },
        defaultProps: {
          color: "success",
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme }) => [
            {
              textDecoration: "underline",
              textDecorationColor: theme.vars.palette.primary.main,
              textDecorationThickness: toRem(2),
              textUnderlineOffset: toRem(5),
              color: theme.vars.palette.text.secondary,
              "&:hover": {
                color: theme.vars.palette.text.primary,
                textDecorationColor: theme.vars.palette.primary.dark,
              },
              "&:focus-visible, &.Mui-focusVisible": {
                outline: "auto",
              },
            },
            theme.applyStyles("dark", {
              textDecorationColor: theme.vars.palette.shadow.contrastText,
              "&:hover": {
                textDecorationColor: theme.vars.palette.shadow.contrastText,
              },
            }),
          ],
        },
      },
      MuiCssBaseline: {
        styleOverrides: ({ vars, breakpoints }) => ({
          html: {
            // Important! must be in px
            fontSize: `${htmlFontSize}px`,
          },
          "*": {
            scrollbarWidth: "thin",
            // Google chrome has native styling for button that gives Arial
            fontFamily: "ApercuPro, Helvetica, sans-serif",
          },
          a: {
            textDecoration: "none",
          },
          'a:not([class^="Mui"],.not-text)': {
            textDecoration: "underline",
            textDecorationColor: vars.palette.primary.main,
            textDecorationThickness: toRem(2),
            textUnderlineOffset: toRem(5),
            color: vars.palette.text.secondary,
            "&:hover": {
              color: vars.palette.text.primary,
              textDecorationColor: vars.palette.primary.dark,
            },
            "&:focus-visible": {
              outline: "auto",
            },
            "[data-dark] &": {
              textDecorationColor: vars.palette.shadow.contrastText,
              "&:hover": {
                textDecorationColor: vars.palette.shadow.contrastText,
              },
            },
          },
          [breakpoints.down("md")]: {
            "#kindly-chat-api [class*=KindlyChatWrapper]": isApp
              ? {
                  bottom: toRem(96),
                }
              : undefined,
          },
        }),
      },
    },
  });
  if (customizedTypography) {
    bnTheme.typography.body1 = {
      ...bnTheme.typography.body1,
      fontSize: toRem(16),
      [bnTheme.breakpoints.down("md")]: {
        fontSize: toRem(13),
      },
    };
    bnTheme.typography.body2 = {
      ...bnTheme.typography.body2,
      fontSize: toRem(16),
      [bnTheme.breakpoints.down("md")]: {
        fontSize: toRem(13),
      },
    };
  }

  return responsiveFontSizes(bnTheme, {
    breakpoints: ["md"],
  });
};

export const emotionCache = setupSsrCache;

interface IBNThemeMode {
  mode: "light" | "dark";
  darkThemeEnabled?: boolean;
  locale?: string;
}

export const BNThemeMode = React.createContext<IBNThemeMode>({
  mode: "light",
  darkThemeEnabled: false,
});
interface BNThemeModeProviderProps {
  darkThemeEnabled?: boolean;
  locale?: string;
  children: ReactNode;
}
const BNThemeModeProvider: React.FC<BNThemeModeProviderProps> = ({ darkThemeEnabled, locale, children }) => {
  const { mode, setMode, systemMode } = useColorScheme();
  const modeToRender = (mode === "system" ? systemMode : mode) ?? "light";

  useKeyboardToggle("m", () => {
    if (localStorage?.getItem("mui-mode") === "dark") {
      setMode("light");
    } else {
      setMode("dark");
    }
  });

  useEffect(() => {
    if (mode) {
      sendMessageToReactNative({
        action: Action.ThemeChanged,
        data: { mode },
      });
    }
  }, [mode]);

  return (
    <BNThemeMode.Provider
      value={{
        mode: !darkThemeEnabled ? "light" : modeToRender,
        darkThemeEnabled,
        locale,
      }}
    >
      {children}
    </BNThemeMode.Provider>
  );
};

interface BNMaterialUIProps {
  htmlFontSize: number;
  darkThemeEnabled?: boolean;
  cssBaseLineEnabled?: boolean;
  children?: React.ReactNode;
  emotionCacheKeySuffix?: string;
  emotionCache?: EmotionCache;
  isApp?: boolean;
  locale?: string;
  customizedTypography?: boolean;
}
/**
 * Same Theme for Material UI in different apps.
 *
 * @param htmlFontSize The font-size of the html element. Browser default is 16px, but in ppcms we use 10px, since that gives 1rem = 10px
 */
export const BNMaterialUI: React.FC<BNMaterialUIProps> = ({
  children,
  cssBaseLineEnabled,
  darkThemeEnabled,
  htmlFontSize,
  emotionCacheKeySuffix,
  isApp,
  locale,
  emotionCache: emotionCacheProp,
  customizedTypography,
}) => {
  // App on iOS will change the size of the root html font-size to scale
  const scaledFontSize = useRootFontSize(htmlFontSize);

  const cache = emotionCacheProp ?? emotionCache(emotionCacheKeySuffix);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={bnMuiTheme(htmlFontSize, scaledFontSize, isApp, darkThemeEnabled, customizedTypography)}>
        <BNThemeModeProvider darkThemeEnabled={darkThemeEnabled} locale={locale}>
          {cssBaseLineEnabled && <CssBaseline />}
          {/* Global styles. ios styling is added because Ios uses hover even though it doesnt have hover */}
          <GlobalStyles
            styles={(theme) => {
              const darkSvgInvert = theme.applyStyles("dark", {
                filter: "invert(1) hue-rotate(180deg)",
              });
              return {
                ".ios.ios.ios .MuiInput-input:focus": {
                  borderColor: theme.vars.palette.secondary.dark,
                },
                ".ios.ios .MuiInput-input:hover": {
                  borderColor: theme.vars.palette.shadow.dark,
                },
                ".ios.ios.ios &.Mui-focused .MuiInputAdornment-standard,.ios.ios.ios &.Mui-focused .MuiAutocomplete-endAdornment":
                  {
                    borderColor: theme.vars.palette.secondary.dark,
                  },
                ".ios.ios &:hover .MuiInputAdornment-standard": {
                  borderColor: theme.vars.palette.shadow.dark,
                },
                'svg:not([class*="Mui"]):not(.no-color-invert)': {
                  ...darkSvgInvert,
                },
                'img[src$=".svg"]:not(.no-color-invert)': {
                  ...darkSvgInvert,
                },
                'img[src^="data:image/svg+xml"]:not(.no-color-invert)': {
                  ...darkSvgInvert,
                },
                'svg[class*="icon"]:not(.no-color-invert), img[class*="icon"][src$=".svg"]:not(.no-color-invert), img[class*="icon"][src^="data:image/svg+xml"]:not(.no-color-invert)':
                  theme.applyStyles("dark", {
                    filter: "brightness(0) invert(1)",
                  }),
              };
            }}
          />
          <GlobalStyles
            styles={`
              .svg-to-white svg,
              .svg-to-white img[src$=".svg"],
              .svg-to-white img[src*="data:image/svg+xml"] {
                filter: brightness(0) invert(1);
              }
            `}
          />
          {children}
          <DevPanel />
        </BNThemeModeProvider>
      </ThemeProvider>
    </CacheProvider>
  );
};

function setupSsrCache(emotionCacheKeySuffix?: string) {
  const suffix = translateNumbersToLowercaseLetters(emotionCacheKeySuffix);
  return createCache({
    key: `bnmui${suffix || ""}`,
  }) as any;
}
